import { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import { pick } from '../../utils/pick';
import { getDefaultPropName, getOnChangeName } from '../../utils/PropNames';
import { jsx as _jsx } from "react/jsx-runtime";
function getComponentName(Component, fallback = 'Component') {
  if (!Component) {
    return fallback;
  }
  return Component.displayName || Component.name || Component.type && (Component.type.displayName || Component.type.name) || fallback;
}
const DEFAULT_FIELDS = ['value'];
function getDefaultProps(component, fields) {
  const defaultProps = component.defaultProps || {};
  return fields.reduce((defaults, field) => {
    if (Object.prototype.hasOwnProperty.call(defaultProps, field)) {
      defaults[getDefaultPropName(field)] = defaultProps[field];
    }
    return defaults;
  }, {});
}
const PREV_PROP_PREFIX = 'prev_';
const toPrevPropKey = field => `${PREV_PROP_PREFIX}${field}`;
export function Controllable(InputComponent, fields = DEFAULT_FIELDS) {
  const onChangeKeys = fields.map(getOnChangeName);
  const defaultPropsFromInput = getDefaultProps(InputComponent, fields);
  const ControlledComponent = /*#__PURE__*/forwardRef((props, ref) => {
    const mergedProps = useMemo(() => {
      return Object.assign({}, defaultPropsFromInput, props);
    }, [props]);
    const defaults = pick(mergedProps, fields.map(getDefaultPropName));
    const [state, setState] = useState(() => {
      const initialState = {};
      fields.forEach(field => {
        initialState[field] = mergedProps[field] !== undefined ? mergedProps[field] : defaults[getDefaultPropName(field)];
        initialState[toPrevPropKey(field)] = mergedProps[field];
      });
      return initialState;
    });
    useEffect(() => {
      const newState = {};
      let hasChanges = false;
      fields.forEach(field => {
        const prevPropKey = toPrevPropKey(field);
        const defaultPropKey = getDefaultPropName(field);
        if (mergedProps[field] !== state[prevPropKey]) {
          if (mergedProps[field] !== undefined) {
            newState[field] = mergedProps[field];
          } else if (state[field] !== defaults[defaultPropKey]) {
            newState[field] = defaults[defaultPropKey];
          }
          newState[prevPropKey] = mergedProps[field];
          hasChanges = true;
        }
      });
      if (hasChanges) {
        setState(prevState => Object.assign({}, prevState, newState));
      }
    }, [state, mergedProps, defaults]);
    const handleChange = useCallback((field, evt) => {
      setState(prevState => Object.assign({}, prevState, {
        [field]: evt.target.value
      }));
      const onChange = mergedProps[getOnChangeName(field)];
      if (typeof onChange === 'function') {
        onChange(evt);
      }
    }, [mergedProps]);
    const handlers = {};
    onChangeKeys.forEach((onChangeKey, i) => {
      handlers[onChangeKey] = evt => handleChange(fields[i], evt);
    });
    const getProps = () => {
      const applicableProps = {};
      Object.keys(mergedProps).forEach(key => {
        if (fields.map(getDefaultPropName).indexOf(key) === -1 && mergedProps[key] !== undefined) applicableProps[key] = mergedProps[key];
      });
      const applicableState = {};
      fields.forEach(field => {
        applicableState[field] = state[field];
      });
      return Object.assign({}, applicableState, applicableProps, handlers);
    };
    const componentProps = Object.assign({}, getProps(), {
      ref
    });
    return /*#__PURE__*/_jsx(InputComponent, Object.assign({}, componentProps));
  });
  ControlledComponent.displayName = `Controllable(${getComponentName(InputComponent)})`;
  return ControlledComponent;
}